import { isAttributionModel, type TAttributionModel } from '@feathr/blackbox';
import { moment, TimeFormat } from '@feathr/hooks';

export type TTimeframePreset =
  | '7d'
  | '30d'
  | '90d'
  | 'mtd'
  | 'last_month'
  | 'qtd'
  | 'ytd'
  | 'custom';

export type TDateRange = [start: string, end: string];

export type { TAttributionModel };

export interface IDashboardUrlState {
  timeframe: TTimeframePreset;
  start?: string;
  end?: string;
  attr: TAttributionModel;
  projects: string[];
  types: string[];
  archived: boolean;
}

export const DEFAULT_URL_STATE: IDashboardUrlState = {
  timeframe: '30d',
  attr: 'last_touch',
  projects: [],
  types: [],
  archived: false,
};

/**
 * Custom-range cap from the Conversions tech eval (#7670 §7). The dashboard's
 * date picker may not exceed this many days from start to end.
 */
export const MAX_CUSTOM_RANGE_DAYS = 365;

const TIMEFRAME_PRESETS: readonly TTimeframePreset[] = [
  '7d',
  '30d',
  '90d',
  'mtd',
  'last_month',
  'qtd',
  'ytd',
  'custom',
];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isTimeframePreset(value: string | null): value is TTimeframePreset {
  return value !== null && (TIMEFRAME_PRESETS as readonly string[]).includes(value);
}

function parseList(raw: string | null): string[] {
  if (!raw) {
    return [];
  }
  return raw
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

/**
 * Parse a `location.search` string into the dashboard's typed URL state. Unknown
 * or malformed values fall back to defaults silently — the URL is shareable so
 * we should never throw on user-supplied input.
 */
export function parseUrlState(search: string): IDashboardUrlState {
  const params = new URLSearchParams(search);

  const rawTimeframe = params.get('timeframe');
  const timeframe = isTimeframePreset(rawTimeframe) ? rawTimeframe : DEFAULT_URL_STATE.timeframe;

  const rawAttr = params.get('attr');
  const attr = rawAttr !== null && isAttributionModel(rawAttr) ? rawAttr : DEFAULT_URL_STATE.attr;

  const state: IDashboardUrlState = {
    timeframe,
    attr,
    projects: parseList(params.get('projects')),
    types: parseList(params.get('types')),
    archived: params.get('archived') === '1',
  };

  if (timeframe === 'custom') {
    const rawStart = params.get('start');
    const rawEnd = params.get('end');
    if (rawStart && ISO_DATE.test(rawStart) && rawEnd && ISO_DATE.test(rawEnd)) {
      state.start = rawStart;
      state.end = rawEnd;
    } else {
      state.timeframe = DEFAULT_URL_STATE.timeframe;
    }
  }

  return state;
}

/**
 * Serialize the dashboard's URL state to a `URLSearchParams`-compatible string.
 * Default-valued fields are omitted so URLs stay short and shareable.
 */
export function serializeUrlState(state: IDashboardUrlState): string {
  const params = new URLSearchParams();

  if (state.timeframe !== DEFAULT_URL_STATE.timeframe) {
    params.set('timeframe', state.timeframe);
  }
  if (state.timeframe === 'custom' && state.start && state.end) {
    params.set('start', state.start);
    params.set('end', state.end);
  }
  if (state.attr !== DEFAULT_URL_STATE.attr) {
    params.set('attr', state.attr);
  }
  if (state.projects.length > 0) {
    params.set('projects', state.projects.join(','));
  }
  if (state.types.length > 0) {
    params.set('types', state.types.join(','));
  }
  if (state.archived) {
    params.set('archived', '1');
  }

  return params.toString();
}

/**
 * Resolves a preset to a concrete `[start, end]` window. For `'custom'`, the
 * caller supplies the range — this function returns the passed-through range.
 */
export function presetToRange(preset: TTimeframePreset, currentRange: TDateRange): TDateRange {
  if (preset === 'custom') {
    return currentRange;
  }
  const today = moment.utc();
  const todayStr = today.format(TimeFormat.isoDate);
  switch (preset) {
    case '7d':
      return [today.clone().subtract(7, 'days').format(TimeFormat.isoDate), todayStr];

    case '30d':
      return [today.clone().subtract(30, 'days').format(TimeFormat.isoDate), todayStr];

    case '90d':
      return [today.clone().subtract(90, 'days').format(TimeFormat.isoDate), todayStr];

    case 'mtd':
      return [today.clone().startOf('month').format(TimeFormat.isoDate), todayStr];

    case 'last_month': {
      const lastMonth = today.clone().subtract(1, 'month');
      return [
        lastMonth.clone().startOf('month').format(TimeFormat.isoDate),
        lastMonth.clone().endOf('month').format(TimeFormat.isoDate),
      ];
    }

    case 'qtd':
      return [today.clone().startOf('quarter').format(TimeFormat.isoDate), todayStr];

    case 'ytd':
      return [today.clone().startOf('year').format(TimeFormat.isoDate), todayStr];
  }
}

/**
 * Clamp a custom date range so it never exceeds MAX_CUSTOM_RANGE_DAYS. If start
 * is after end the inputs are returned unchanged — callers should treat that as
 * a validation error upstream.
 */
export function clampCustomRange(
  start: string,
  end: string,
  maxDays: number = MAX_CUSTOM_RANGE_DAYS,
): { start: string; end: string } {
  if (!ISO_DATE.test(start) || !ISO_DATE.test(end)) {
    return { start, end };
  }
  const startMs = Date.parse(`${start}T00:00:00Z`);
  const endMs = Date.parse(`${end}T00:00:00Z`);
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || startMs > endMs) {
    return { start, end };
  }
  const spanDays = (endMs - startMs) / 86_400_000;
  if (spanDays <= maxDays) {
    return { start, end };
  }
  const clampedStartMs = endMs - maxDays * 86_400_000;
  const clampedStart = new Date(clampedStartMs).toISOString().split('T')[0];
  return { start: clampedStart, end };
}
